import * as React from "react"
import PropTypes from "prop-types"
import "./PresetState.scss"

// colGridApi required to get column definition for column headers. can pass colGridApi from report pages but not from All Reports and Account Subscription Settings pages
const PresetState = ({ presetName, json, colGridApi }) => {
  const { column_state, filter_state, sort_state } = json

  const getColHeader = (colId) => {
    const colDef = colGridApi?.getColumn(colId)?.colDef
    if (colDef?.headerName) return colDef.headerName
    if (colId) {
      // convert to title case
      return colId
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    }
  }

  // filtered columns

  const filteredHeaders = React.useMemo(() => {
    if (!filter_state || Object.keys(filter_state).length === 0) return null
    return Object.keys(filter_state).map((key) => getColHeader(key))
  }, [filter_state])

  // sorted columns

  const sortedHeaders = React.useMemo(() => {
    if (!sort_state || sort_state.length === 0) return null
    return sort_state.map((col) => `${getColHeader(col.colId)} (${col.sort})`) // col.sort: "(asc)" || "(desc)"
  }, [sort_state])

  // pinned columns

  const getPinnedCols = (position = "left") => {
    if (!column_state || column_state.length === 0) return null
    return column_state.filter((col) => col.pinned == position).map((col) => getColHeader(col.colId))
  }

  const pinnedLeftHeaders = React.useMemo(() => {
    return getPinnedCols("left")
  }, [column_state])

  const pinnedRightHeaders = React.useMemo(() => {
    return getPinnedCols("right")
  }, [column_state])

  // hidden columns

  const hiddenHeaders = React.useMemo(() => {
    if (!column_state || column_state.length === 0) return null
    return column_state
      .filter((col) => col.hide === true && !col.colId.includes("_prs"))
      .map((col) => getColHeader(col.colId))
  }, [column_state])

  const renderStateWithCols = (title, headers, ellipsisMax) => {
    if (!headers || headers.length === 0) return null
    let text = headers.join(", ")
    if (ellipsisMax && headers.length > ellipsisMax) {
      text = headers.slice(0, ellipsisMax).join(", ") + "..."
    }
    return (
      <li className="preset-item">
        <span className="preset-field-label">
          {title}&nbsp;({headers.length}):
        </span>{" "}
        {text}
      </li>
    )
  }

  // states without column headers for All Reports and Account Subscription Settings pages

  const renderStateCounts = (title, array) => {
    if (!array || array.length === 0) return null
    return `${title} (${array.length})`
  }

  const renderStatesWoutCols = () => {
    let states = []
    const filtered = renderStateCounts("Filtered", filteredHeaders)
    if (filtered) states.push(filtered)
    const sorted = renderStateCounts("Sorted", sortedHeaders)
    if (sorted) states.push(sorted)
    const pinnedLeft = renderStateCounts("Pinned Left", pinnedLeftHeaders)
    if (pinnedLeft) states.push(pinnedLeft)
    const pinnedRight = renderStateCounts("Pinned Right", pinnedRightHeaders)
    if (pinnedRight) states.push(pinnedRight)
    const hidden = renderStateCounts("Hidden", hiddenHeaders)
    if (hidden) states.push(hidden)
    if (states.length > 0) return states.join(", ")
    return null
  }

  if (!json) return null

  return (
    <div className="preset-state">
      {presetName && <div className="preset-name">Preset "{presetName}"</div>}
      {colGridApi ? (
        <ul>
          {renderStateWithCols("Filtered", filteredHeaders)}
          {renderStateWithCols("Sorted", sortedHeaders)}
          {renderStateWithCols("Pinned Left", pinnedLeftHeaders)}
          {renderStateWithCols("Pinned Right", pinnedRightHeaders)}
          {/* hidden showing three maximum items */}
          {renderStateWithCols("Hidden", hiddenHeaders, 3)}
        </ul>
      ) : (
        <div>{renderStatesWoutCols()}</div>
      )}
    </div>
  )
}

PresetState.propTypes = {
  json: PropTypes.shape({
    column_state: PropTypes.array.isRequired,
    filter_state: PropTypes.object.isRequired,
    sort_state: PropTypes.array.isRequired,
  }).isRequired,
  colGridApi: PropTypes.object.isRequired,
}

export default PresetState
